/**
 * Vanilla JS Google Maps initialization.
 * Discovers elements with [data-map] and initializes Google Maps instances.
 *
 * Supported attributes:
 *   data-map            - presence triggers initialization
 *   data-map-markers    - JSON array of marker objects with lat, lng, and optional info fields
 *   data-map-zoom       - zoom level (number, default 13)
 *   data-map-center-lat - center latitude (default from first marker or 47.4875)
 *   data-map-center-lng - center longitude (default from first marker or 12.5311)
 */

let mapsApiLoaded = false;
let mapsApiLoading = false;
const mapsApiCallbacks = [];

function loadGoogleMapsApi(callback) {
    if (mapsApiLoaded) {
        callback();
        return;
    }

    mapsApiCallbacks.push(callback);

    if (mapsApiLoading) return;
    mapsApiLoading = true;

    const apiKey = __GOOGLE_MAPS_API_KEY__;

    if (!apiKey) {
        console.warn('Google Maps API key not found. Set GOOGLE_MAPS_API_KEY.');
        return;
    }

    window.__initGoogleMaps = function () {
        mapsApiLoaded = true;
        mapsApiCallbacks.forEach((cb) => cb());
        mapsApiCallbacks.length = 0;
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__initGoogleMaps`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
}

function initMap(el) {
    if (el._googleMap) return;

    const zoom = parseInt(el.dataset.mapZoom, 10) || 13;

    let markers = [];
    if (el.dataset.mapMarkers) {
        try {
            markers = JSON.parse(el.dataset.mapMarkers);
        } catch (e) {
            console.warn('Invalid data-map-markers JSON', e);
        }
    }

    const defaultLat = markers.length > 0 ? parseFloat(markers[0].lat || markers[0].latitude) : 47.48750172196571;
    const defaultLng = markers.length > 0 ? parseFloat(markers[0].lng || markers[0].longitude) : 12.531109346357393;

    const centerLat = parseFloat(el.dataset.mapCenterLat) || defaultLat;
    const centerLng = parseFloat(el.dataset.mapCenterLng) || defaultLng;

    const map = new google.maps.Map(el, {
        zoom: zoom,
        center: { lat: centerLat, lng: centerLng },
        clickableIcons: true,
        draggable: true
    });

    el._googleMap = map;

    markers.forEach((markerData) => {
        const lat = parseFloat(markerData.lat || markerData.latitude);
        const lng = parseFloat(markerData.lng || markerData.longitude);

        if (isNaN(lat) || isNaN(lng)) return;

        const marker = new google.maps.Marker({
            position: { lat, lng },
            map: map,
            title: markerData.name || ''
        });

        // Build info window content if data is available
        if (markerData.name) {
            let content = '<div class="map-info-window">';
            if (markerData.image) {
                content += `<img src="${markerData.image}" alt="${markerData.name}" style="max-width:200px;margin-bottom:8px;">`;
            }
            content += `<strong>${markerData.name}</strong>`;
            if (markerData.street) {
                content += `<p>${markerData.street} ${markerData.number || ''}, ${markerData.postalCode || ''} ${markerData.city || ''}</p>`;
            }
            if (markerData.url) {
                content += `<a href="${markerData.url}">Details</a>`;
            }
            content += '</div>';

            const infoWindow = new google.maps.InfoWindow({ content });

            marker.addListener('click', () => {
                infoWindow.open(map, marker);
            });
        }
    });
}

function initAll(root) {
    const elements = (root || document).querySelectorAll('[data-map]');
    if (elements.length === 0) return;

    loadGoogleMapsApi(() => {
        elements.forEach((el) => {
            if (!el._googleMap) {
                initMap(el);
            }
        });
    });
}

// Auto-init on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => initAll());

// Re-init after HTMX swaps
document.body.addEventListener('htmx:afterSettle', (e) => {
    initAll(e.detail.target);
});

// Export for manual use
window.initMaps = initAll;
